// Geometry for hosted inline placement: the dock's single media runtime is
// positioned (position: fixed) exactly over the inline post's media frame while
// that frame is on screen. The frame lives inside a scrolling, sliding panel,
// so the dock follows its rect every frame and clips itself to the part of the
// frame the user can actually see (scroll container bounds, sidebar occlusion).

export const HOSTED_PLACEMENT_BORDER_RADIUS_PX = 4

// How close (px) a clipping ancestor's corner must sit to the frame's corner
// for that ancestor's curve to be the one the frame shows there.
const CORNER_MATCH_TOLERANCE_PX = 1.5

// Each corner, the two sides that meet there, and its computed radius property.
const HOSTED_CORNERS = [
    ['topLeft', 'top', 'left', 'borderTopLeftRadius'],
    ['topRight', 'top', 'right', 'borderTopRightRadius'],
    ['bottomRight', 'bottom', 'right', 'borderBottomRightRadius'],
    ['bottomLeft', 'bottom', 'left', 'borderBottomLeftRadius'],
]

function cssPx(value) {
    const parsed = Number.parseFloat(value)
    return Number.isFinite(parsed) && !String(value).includes('%') ? parsed : 0
}

/**
 * The corner radii the post's media frame shows on screen, per corner, or null
 * when nothing rounds it.
 *
 * A frame is usually rounded by an ancestor's clip, not by its own radius: an
 * ordinal's artwork sits flush in its card's top corners and the card's
 * rounded overflow clip curves it. The hosted runtime is not inside that clip
 * (it is laid over the frame, or moved into it below the card's layers), so it
 * restates the curve itself — otherwise the playing video's letterbox paints
 * square corners over the card's rounded ring.
 */
export function resolveHostedCornerRadii(originEl, rect = null, getStyle = null) {
    const resolveStyle =
        getStyle ||
        (typeof window !== 'undefined' && typeof window.getComputedStyle === 'function'
            ? element => window.getComputedStyle(element)
            : null)
    const frame = rect || originEl?.getBoundingClientRect?.()
    if (!originEl || !frame || !resolveStyle) return null

    const radii = { topLeft: 0, topRight: 0, bottomRight: 0, bottomLeft: 0 }
    const near = (a, b) => Math.abs(a - b) <= CORNER_MATCH_TOLERANCE_PX
    for (
        let element = originEl;
        element && element.nodeType === 1;
        element = element.parentElement
    ) {
        const style = resolveStyle(element)
        if (!style) continue
        const clips =
            element === originEl || style.overflowX !== 'visible' || style.overflowY !== 'visible'
        if (!clips) continue

        // The clip follows the padding box, so its curve is the outer radius
        // less the border.
        const box = element.getBoundingClientRect()
        const border = {
            top: cssPx(style.borderTopWidth),
            right: cssPx(style.borderRightWidth),
            bottom: cssPx(style.borderBottomWidth),
            left: cssPx(style.borderLeftWidth),
        }
        const clip = {
            top: box.top + border.top,
            right: box.right - border.right,
            bottom: box.bottom - border.bottom,
            left: box.left + border.left,
        }
        for (const [corner, y, x, radiusProperty] of HOSTED_CORNERS) {
            if (!near(clip[y], frame[y]) || !near(clip[x], frame[x])) continue
            const inner = Math.max(0, cssPx(style[radiusProperty]) - Math.max(border[y], border[x]))
            radii[corner] = Math.max(radii[corner], Math.round(inner))
        }
    }

    return radii.topLeft || radii.topRight || radii.bottomRight || radii.bottomLeft ? radii : null
}

/** Whether two resolveHostedCornerRadii results draw the same curve. */
export function sameHostedCornerRadii(a, b) {
    if (!a || !b) return !a && !b
    return HOSTED_CORNERS.every(([corner]) => a[corner] === b[corner])
}

// ` round …` for an inset() clip: per-corner radii from resolveHostedCornerRadii,
// or one radius (the dock's default when the frame is not rounded).
function formatHostedRound(borderRadius) {
    if (borderRadius && typeof borderRadius === 'object') {
        const { topLeft = 0, topRight = 0, bottomRight = 0, bottomLeft = 0 } = borderRadius
        return ` round ${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`
    }
    const radius = Number.isFinite(borderRadius) ? borderRadius : HOSTED_PLACEMENT_BORDER_RADIUS_PX
    return radius > 0 ? ` round ${radius}px` : ''
}

/** The clip-path a surface moved into the frame takes: the frame's own curve. */
export function buildHostedFlowClipPath(borderRadius) {
    return borderRadius ? `inset(0px${formatHostedRound(borderRadius)})` : 'none'
}

// A first YouTube play has no owner until its iframe is ready. Navigation
// during that interval must retain the requested runtime just like a playing
// one; a completed user pause has no pending play request and is released.
export function shouldKeepHostedRuntime(mediaKey, snapshot, pendingMediaKey) {
    if (!mediaKey || snapshot?.nowPlayingMediaKey !== mediaKey) return false
    return Boolean(snapshot.isPlaying || pendingMediaKey === mediaKey)
}

// The anchor the dock binds itself to while hosting a post's media frame.
export const HOSTED_ANCHOR_NAME = '--treechat-hosted-media'
export const HOSTED_CORNER_MORPH_ID = 'treechat-corner-morph'

/**
 * Chromium's state-preserving DOM move keeps iframe browsing contexts alive
 * while moving their containing surface. When available, hosted media can live
 * inside the post's frame and inherit its scroll and panel transforms directly.
 */
export function supportsStatePreservingMove(parentPrototype = null) {
    try {
        const resolvedPrototype =
            parentPrototype || (typeof Element !== 'undefined' ? Element.prototype : null)
        return typeof resolvedPrototype?.moveBefore === 'function'
    } catch {
        return false
    }
}

/**
 * Move a mounted presentation without disconnecting its iframe or resetting
 * playback. Both the presentation and destination must already be connected.
 */
export function moveHostedPresentation(element, destination, before = null) {
    if (!element || !destination || typeof destination.moveBefore !== 'function') return false
    try {
        destination.moveBefore(element, before)
        return element.parentNode === destination
    } catch {
        return false
    }
}

/** Restore a moved presentation immediately after its inert home marker. */
export function restoreHostedPresentation(element, homeMarker) {
    const destination = homeMarker?.parentNode || null
    if (!element || !destination) return false
    if (element.parentNode === destination && homeMarker.nextSibling === element) return true
    return moveHostedPresentation(element, destination, homeMarker.nextSibling)
}

// The tracking loop changes transform and clipPath directly. Declaring their
// neutral values in React's hosted style makes React clear the final imperative
// values in the same commit that returns the dock to its corner layout.
export const HOSTED_ANCHORED_RESET_STYLE = {
    right: 'auto',
    bottom: 'auto',
    maxWidth: 'none',
    transform: 'none',
    clipPath: 'none',
}

/**
 * Move the shared hosted anchor name from one origin to another. Only one
 * origin may own this name: when multiple elements expose it, CSS resolves the
 * target by tree order rather than by which post the user just played.
 */
export function replaceHostedAnchorElement(previousElement, nextElement) {
    try {
        previousElement?.style?.removeProperty('anchor-name')
    } catch {}

    if (!nextElement?.style) return false
    try {
        nextElement.style.setProperty('anchor-name', HOSTED_ANCHOR_NAME)
        return true
    } catch {
        return false
    }
}

/**
 * Reset styles written by the anchored tracking loop before a different
 * origin starts using the same dock element.
 */
export function resetHostedAnchoredStyle(element) {
    if (!element?.style) return false
    try {
        // A corner morph overrides inline transforms until it finishes. Stop
        // it before the anchor tracker measures and accumulates corrections.
        for (const animation of element.getAnimations?.() || []) {
            if (animation.id === HOSTED_CORNER_MORPH_ID) animation.cancel()
        }
        element.style.transform = 'none'
        element.style.clipPath = 'none'
        return true
    } catch {
        return false
    }
}

/**
 * Fullscreen CSS owns the root's geometry. Applying residual corrections while
 * it forces transform:none would accumulate an offset that appears on exit.
 */
export function isHostedSurfaceFullscreen(element, fullscreenDocument = null) {
    if (!element) return false
    const resolvedDocument =
        fullscreenDocument || (typeof document !== 'undefined' ? document : null)
    if (!resolvedDocument) return false
    return (
        resolvedDocument.fullscreenElement === element ||
        resolvedDocument.webkitFullscreenElement === element
    )
}

/**
 * CSS anchor positioning lets the engine glue the dock to the post's frame.
 * That matters because scrolling is asynchronous: a container scrolled on the
 * compositor moves the post before the main thread hears about it, so a
 * JS-positioned dock trails the scroll and visibly catches up afterwards.
 * Anchored, the engine moves both together.
 *
 * It resolves against *layout* position only, so it does not follow an ancestor
 * transform (a panel slide) — the tracking loop still corrects that residual.
 */
export function supportsAnchorPositioning(cssApi = null) {
    try {
        const resolvedCss = cssApi || (typeof CSS !== 'undefined' ? CSS : null)
        return Boolean(
            typeof resolvedCss?.supports === 'function' &&
                resolvedCss.supports('anchor-name: --a') &&
                resolvedCss.supports('position-anchor: --a') &&
                resolvedCss.supports('top', 'anchor(top)') &&
                resolvedCss.supports('width', 'anchor-size(width)'),
        )
    } catch {
        return false
    }
}

/**
 * How far the dock currently sits from the frame it is anchored to. Zero while
 * only scrolling; non-zero while an ancestor transform (a panel slide) moves
 * the frame without moving its layout box.
 */
export function computeAnchorResidual(originRect, dockRect) {
    if (!originRect || !dockRect) return { x: 0, y: 0 }
    return {
        x: Math.round(originRect.left - dockRect.left),
        y: Math.round(originRect.top - dockRect.top),
    }
}

/**
 * Applies the anchored placement: the engine owns position and size, so JS
 * writes only the residual transform and the clip, and only when they change.
 */
export function applyHostedAnchoredStyle(element, residual, clip = null, options = {}) {
    if (!element?.style) return null

    const round = formatHostedRound(options.borderRadius)
    const style = {
        transform:
            residual && (residual.x || residual.y)
                ? `translate3d(${residual.x}px, ${residual.y}px, 0)`
                : 'none',
        clipPath: clip
            ? `inset(${clip.top}px ${clip.right}px ${clip.bottom}px ${clip.left}px${round})`
            : `inset(0px${round})`,
    }

    const previous = options.previous || null
    if (
        previous &&
        previous.transform === style.transform &&
        previous.clipPath === style.clipPath
    ) {
        return previous
    }
    if (!previous || previous.transform !== style.transform)
        element.style.transform = style.transform
    if (!previous || previous.clipPath !== style.clipPath) element.style.clipPath = style.clipPath
    return style
}

function finite(value) {
    return Number.isFinite(value) ? value : 0
}

/**
 * How much of the origin rect lies outside the visible bounds, per edge, in
 * px. Returns null when the whole rect is visible (no clipping needed).
 */
export function computeHostedClipInset(rect, bounds) {
    if (!rect || !bounds) return null

    const top = Math.max(0, finite(bounds.top) - finite(rect.top))
    const left = Math.max(0, finite(bounds.left) - finite(rect.left))
    const right = Math.max(0, finite(rect.right) - finite(bounds.right))
    const bottom = Math.max(0, finite(rect.bottom) - finite(bounds.bottom))

    if (top <= 0 && left <= 0 && right <= 0 && bottom <= 0) return null
    return {
        top: Math.round(top),
        right: Math.round(right),
        bottom: Math.round(bottom),
        left: Math.round(left),
    }
}

/**
 * Inline style placing the dock over an origin rect. Translation (not
 * left/top) keeps per-frame updates on the compositor.
 */
export function buildHostedPlacementStyle(rect, clip = null, options = {}) {
    if (!rect || !(rect.width > 0) || !(rect.height > 0)) return null

    const round = formatHostedRound(options.borderRadius)
    const clipPath = clip
        ? `inset(${clip.top}px ${clip.right}px ${clip.bottom}px ${clip.left}px${round})`
        : `inset(0px${round})`

    return {
        transform: `translate3d(${Math.round(rect.left)}px, ${Math.round(rect.top)}px, 0)`,
        width: `${Math.round(rect.width)}px`,
        height: `${Math.round(rect.height)}px`,
        clipPath,
    }
}

/**
 * Writes the placement straight onto the element, bypassing React for the
 * per-frame tracking loop.
 *
 * Only properties that actually changed are written: `width`/`height` dirty
 * layout, so re-writing them every frame would force a full layout on the next
 * frame's rect read, while a still frame should cost no writes at all. Pass the
 * previously applied style as `options.previous` and keep the returned value
 * for the next call; the same object comes back when nothing changed.
 */
export function applyHostedPlacementStyle(element, rect, clip = null, options = {}) {
    const style = buildHostedPlacementStyle(rect, clip, options)
    if (!element?.style || !style) return null

    const previous = options.previous || null
    if (
        previous &&
        previous.transform === style.transform &&
        previous.width === style.width &&
        previous.height === style.height &&
        previous.clipPath === style.clipPath
    ) {
        return previous
    }

    if (!previous || previous.width !== style.width) element.style.width = style.width
    if (!previous || previous.height !== style.height) element.style.height = style.height
    if (!previous || previous.transform !== style.transform) {
        element.style.transform = style.transform
    }
    if (!previous || previous.clipPath !== style.clipPath) element.style.clipPath = style.clipPath

    return style
}

/**
 * Whether a tracked origin still qualifies for hosting: it must be the same
 * element the placement started on and at least partly inside the visible
 * bounds. Anything else means the runtime should glide to the dock corner.
 *
 * While the hosted surface is fullscreen the page behind it can reflow the
 * frame out of view (rotating a phone switches the app's layout) without the
 * viewer leaving the video, so only losing the frame itself ends hosting.
 */
export function shouldKeepHostedPlacement({ placement, originEl, surfaceFullscreen = false }) {
    if (!placement || !originEl) return false
    if (placement.element !== originEl) return false
    if (surfaceFullscreen) return true
    return placement.visible === true && Boolean(placement.rect)
}

/**
 * Hosting is ending while its surface is the fullscreen element: leave
 * fullscreen too, or the corner dock is left filling the screen with the
 * post's controls (and their exit button) gone. Returns whether it exited.
 */
export function exitHostedSurfaceFullscreen(elements, fullscreenDocument = null) {
    const resolvedDocument =
        fullscreenDocument || (typeof document !== 'undefined' ? document : null)
    if (!resolvedDocument) return false
    const fullscreenElement =
        resolvedDocument.fullscreenElement || resolvedDocument.webkitFullscreenElement || null
    if (!fullscreenElement) return false
    const ownsFullscreen = elements.some(
        element =>
            element &&
            (element === fullscreenElement || element.contains?.(fullscreenElement) === true),
    )
    if (!ownsFullscreen) return false
    const exit = resolvedDocument.exitFullscreen || resolvedDocument.webkitExitFullscreen
    if (typeof exit !== 'function') return false
    try {
        exit.call(resolvedDocument)?.catch?.(() => {})
    } catch {
        return false
    }
    return true
}

/**
 * The reason a hosted placement ends, for telemetry and tests.
 */
export function resolveHostedDetachReason({ placement, originEl }) {
    if (!placement || placement.element !== originEl) return 'origin-lost'
    if (!placement.rect) return 'origin-hidden'
    if (!placement.visible) return 'origin-offscreen'
    return null
}
